import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Avatar,
  Link,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { fetchCongressMemberByBioguideId } from "../utils/discoverApi";
import { POLICY_NAV_LABEL } from "../constants/policyLabels";
import { theme } from "../theme/colors";

const SITE_URL = "https://civixthread.com";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function getPartyColor(party) {
  if (!party) return "#64748b";
  const p = String(party).toLowerCase();
  if (p.includes("democrat") || p === "d") return "#2563eb";
  if (p.includes("republican") || p === "r") return "#dc2626";
  if (p.includes("independent") || p === "i") return "#7c3aed";
  return "#64748b";
}

function getPartyDisplay(party) {
  if (!party) return "Unknown";
  const p = String(party).toLowerCase();
  if (p.includes("democrat") || p === "d") return "Democrat";
  if (p.includes("republican") || p === "r") return "Republican";
  if (p.includes("independent") || p === "i") return "Independent";
  return party;
}

function getPositionDisplay(position) {
  if (!position) return "Member of Congress";
  const p = String(position).toLowerCase();
  if (p === "senate") return "Senator";
  if (p === "house") return "Representative";
  return position;
}

function getDistrictLabel(member) {
  if (!member) return "";
  const state = member.recent_state || "";
  const district = member.recent_district;
  const position = member.current_position?.toLowerCase();
  if (position === "senate") return state ? `${state} Senator` : "Senator";
  if (position === "house") {
    if (district == null || district === 0) return state ? `${state} At-Large` : "At-Large District";
    return state ? `${state}-${district}` : `District ${district}`;
  }
  return state || "";
}

export default function CongressMemberDetailPage() {
  const { bioguideId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoError, setPhotoError] = useState(false);

  useEffect(() => {
    if (!bioguideId) {
      setLoading(false);
      setError("Missing member ID");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCongressMemberByBioguideId(bioguideId)
      .then((data) => {
        if (!cancelled) setMember(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || "Failed to load member details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [bioguideId]);

  const displayName = member
    ? member.full_name || [member.first_name, member.last_name].filter(Boolean).join(" ") || "Unknown"
    : "";
  const initials = member
    ? [member.first_name?.[0], member.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "?"
    : "?";
  const photoUrl =
    member?.bio_photo_url ||
    (member?.bioguide_id ? `${API_BASE_URL}/api/member_photo/${member.bioguide_id}/` : null);

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", p: 3 }}>
        <CircularProgress size={40} sx={{ color: theme.primary }} />
        <Typography sx={{ mt: 2, color: "#0f172a" }}>Loading member details...</Typography>
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", p: 3 }}>
        <Typography sx={{ color: "#0f172a", textAlign: "center" }}>
          {error || "Member not found"}
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/discover")} sx={{ mt: 2 }}>
          Back to Discover
        </Button>
      </Box>
    );
  }

  const partyColor = getPartyColor(member.party);
  const districtLabel = getDistrictLabel(member);
  const phone = member.phone_number || null;
  const website = member.official_website_url || null;
  const canonicalUrl = `${SITE_URL}/congress/${encodeURIComponent(member.bioguide_id || bioguideId || "")}`;
  const metaDescription = `${displayName} — ${getPositionDisplay(member.current_position)}${districtLabel ? `, ${districtLabel}` : ""}. Contact info and civic data on CivixThread (Congress.gov-sourced).`;

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: 3 }}>
      <Helmet>
        <title>{`${displayName} | CivixThread`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${displayName} | CivixThread`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
        {photoUrl && !photoError ? (
          <Avatar
            src={photoUrl}
            onError={() => setPhotoError(true)}
            sx={{
              width: 120,
              height: 120,
              bgcolor: `${partyColor}20`,
              color: partyColor,
              fontSize: 36,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        ) : (
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: `${partyColor}20`,
              color: partyColor,
              fontSize: 36,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        )}
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", mt: 2, textAlign: "center" }}>
          {displayName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
          <Typography
            component="span"
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: partyColor,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: `${partyColor}20`,
            }}
          >
            {getPartyDisplay(member.party)}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#0f172a" }}>
            {getPositionDisplay(member.current_position)}
          </Typography>
          {districtLabel && (
            <Typography sx={{ fontSize: 14, color: "#475569" }}>{districtLabel}</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {phone && (
          <Link
            href={`tel:${phone.replace(/[^\d+]/g, "")}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: "#0f172a",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <PhoneIcon sx={{ color: theme.primary }} />
            <Typography sx={{ color: "#0f172a" }}>{phone}</Typography>
          </Link>
        )}
        {website && (
          <Link
            href={website.startsWith("http") ? website : `https://${website}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: "#0f172a",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <LanguageIcon sx={{ color: theme.primary }} />
            <Typography sx={{ color: "#0f172a" }}>Official website</Typography>
          </Link>
        )}
        {member.office_address && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <LocationOnIcon sx={{ color: theme.primary, mt: 0.25 }} />
            <Typography sx={{ color: "#0f172a", fontSize: 14 }}>
              {[member.office_address, member.office_address_city, member.office_address_zipcode]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Box>
        )}
      </Box>

      {member.has_civixthread_account && member.civixthread_user_id ? (
        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AccountCircleIcon />}
            onClick={() => navigate(`/profile/${member.civixthread_user_id}`)}
            sx={{
              py: 1.75,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "16px",
              textTransform: "none",
              background: theme.gradient,
              "&:hover": { opacity: 0.95, background: theme.gradient },
            }}
          >
            View CivixThread Profile
          </Button>
        </Box>
      ) : null}

      {/* View Bills — matches mobile: primary gradient, icon + chevron, hint below */}
      <Box sx={{ mt: 3 }}>
        <Box
          component="button"
          onClick={() => {
            const pos = (member.current_position || "").toLowerCase();
            navigate("/bills", {
              state: {
                memberBioguideId: member.bioguide_id,
                memberName: displayName,
                memberPosition: pos === "senate" ? "senate" : "house",
                clearFilters: true,
              },
            });
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            py: 2,
            px: 3,
            border: "none",
            borderRadius: "14px",
            cursor: "pointer",
            background: "linear-gradient(180deg, #047857 0%, #065f46 100%)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "17px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            "&:hover": { opacity: 0.95 },
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 22, mr: 1.25 }} />
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 17 }}>
            View {POLICY_NAV_LABEL}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 22, ml: 1.25 }} />
        </Box>
        <Typography sx={{ fontSize: 15, color: "#0f172a", textAlign: "center", mt: 1.25 }}>
          See bills sponsored and cosponsored by {member.first_name} {member.last_name}
        </Typography>
      </Box>

      {/* Back to Discover — secondary style (mobile uses header back) */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/discover")}
          sx={{
            width: "100%",
            py: 1.5,
            borderRadius: "14px",
            borderColor: "#0f172a",
            color: "#0f172a",
            backgroundColor: "transparent",
            fontWeight: 600,
            fontSize: "15px",
            "&:hover": {
              borderColor: "#0f172a",
              backgroundColor: "rgba(15, 23, 42, 0.04)",
            },
          }}
        >
          Back to Discover
        </Button>
      </Box>
    </Box>
  );
}
